"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { cn } from "@/lib/utils";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  variant?: "default" | "danger";
}

/** Shared confirm/alert dialog — use anywhere a window.confirm()/alert() would otherwise show the browser's own unstyled prompt. Pass the server's own error message through as `message` rather than rewording it. */
export function ConfirmDialog({
  open, onOpenChange, title, message, confirmLabel, cancelLabel = "Cancel", onConfirm, variant = "default",
}: ConfirmDialogProps) {
  const isDanger = variant === "danger";
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-card p-6 shadow-xl outline-none">
          <div className="flex items-start justify-between gap-4">
            <div className={cn("flex size-10 shrink-0 items-center justify-center rounded-full", isDanger ? "bg-red-50 text-red-600" : "bg-sky-50 text-sky-600")}>
              <Icon name="alert-circle" size={18} />
            </div>
            <Dialog.Close
              className="rounded-full p-1 text-muted transition hover:bg-secondary/5 hover:text-secondary"
              aria-label="Close"
            >
              <Icon name="x" size={18} />
            </Dialog.Close>
          </div>
          <Dialog.Title className="mt-4 text-lg font-semibold text-secondary">{title}</Dialog.Title>
          <Dialog.Description className="mt-1.5 text-sm text-muted">{message}</Dialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild>
              <Button variant="outline" size="sm" type="button" className="rounded-full">
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              variant={isDanger ? "danger" : "primary"}
              size="sm"
              type="button"
              className="rounded-full"
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
